'use client'

import {
  AREA_OPTION_ALL,
  HOTEL_SEARCH_ORDER_BY_DEFAULT,
  groupRoomSelectList,
  shouldUseHanareHotelDates,
  shouldUseEbisuHotelDates,
  shouldUseMercureHiroshimaHotelDates,
  shouldUseShinjukuOpeningHotelDates,
  shouldUseGotandaOpeningHotelDates,
} from 'core/bookings'
import type { RoomSelectInfo } from 'core/bookings/types'

import { useMemo } from 'react'
import { isEmpty } from 'lodash'
import { useSearchParams } from 'next/navigation'
import qs from 'qs'
import dayjs from 'lib/dayjs'

import { formatDate } from 'utils'
import {
  DEFAULT_ROOM_GUEST_VALUE,
  HANARE_HOTEL_CONFIG,
  EBISU_HOTEL_CONFIG,
  MERCURE_HIROSHIMA_HOTEL_CONFIG,
  SHINJUKU_OPENING_HOTEL_CONFIG,
  GOTANDA_OPENING_HOTEL_CONFIG,
} from 'utils/constants'
import {
  sanitizeString,
  sanitizeNumeric,
  sanitizeDate,
  sanitizeHotelCode,
} from 'utils/sanitizeSearchParams'
import {
  parseHotelPlanCodes,
  stringifyHotelPlanCodes,
  checkAndSetResaleHotelPlanCodesQueryParams,
} from 'utils/formatHotelPlanCodes'

import useQueryHotelPlanList from '../useQueryHotelPlanList'
import useQueryHotelList from '../useQueryHotelList'
import { useLocale } from '../UseLocale'
import useAppNavigate from '../useAppNavigate'

interface RoomSearchParamsState {
  areaId: string
  hotelCode: string
  amenities: string
  defaultAmenities: { planList: string[]; roomTypeList: string[] }
  checkIn: string
  checkOut: string
  dateRange: (Date | null)[]
  numberRoomGuest: string
  defaultNumberRoomGuest: {
    index: number
    numberOfAdult: number
    numberOfChildren: number
  }[]
  defaultType: 'plan' | 'rooms'
  type: string
  orderBy: string
  defaultOrderBy: string
  searchTimestamp: string
  selectedHotelPlanCodes: string | string[] | null | undefined
  notScrollToSearchResult: boolean
  showResale: boolean | undefined
  isResalePlan: boolean
  isUndated: boolean
  nights: number
  onModifySearchCondition: () => void
  onViewDetail: (selectedRoomList: RoomSelectInfo[]) => void
}

const useRoomSearchParams = (): RoomSearchParamsState => {
  const { navigate } = useAppNavigate()
  const searchParams = useSearchParams()
  const { locale } = useLocale()

  const areaId = sanitizeString(searchParams?.get('areaId')) as string
  const hotelCode = sanitizeHotelCode(searchParams?.get('hotelCode')) as string
  const amenities = searchParams?.get('amenities') as string
  const numberRoomGuest = searchParams?.get('numberRoomGuest') as string
  const type = sanitizeString(searchParams?.get('type')) as string
  const checkIn = sanitizeDate(searchParams?.get('checkIn')) as string
  const checkOut = sanitizeDate(searchParams?.get('checkOut')) as string
  const orderBy = sanitizeString(searchParams?.get('orderBy'))
  const showResale = sanitizeString(searchParams?.get('showResale') as string)
  const isResalePlan = sanitizeString(
    searchParams?.get('isResalePlan') as string,
  )
  const defaultOrderBy = orderBy || (HOTEL_SEARCH_ORDER_BY_DEFAULT as string)
  const searchTimestamp = sanitizeNumeric(
    searchParams?.get('searchTimestamp'),
  ) as string
  const selectedHotelPlanCodes = searchParams?.get('hotelPlanCodes')
  const notScrollToSearchResult = searchParams?.get(
    'notScrollToSearchResult',
  ) as string

  const isUndated = false
  const nights = 1

  const defaultType = (type || 'plan') as 'plan' | 'rooms'

  const parsedSelectedHotelPlanCodes = parseHotelPlanCodes(
    selectedHotelPlanCodes,
  )

  const { data: hotelPlanList } = useQueryHotelPlanList(
    hotelCode as string,
    undefined,
    false,
    !!hotelCode,
  )
  const { data: otherHotelPlanList } = useQueryHotelPlanList(
    hotelCode as string,
    !parsedSelectedHotelPlanCodes
      ? undefined
      : Array.isArray(parsedSelectedHotelPlanCodes)
        ? parsedSelectedHotelPlanCodes
        : [parsedSelectedHotelPlanCodes],
    showResale === 'true',
    !!hotelCode && !!parsedSelectedHotelPlanCodes,
  )

  const combinedPlanList = useMemo(() => {
    const planCodes = new Set<string>()

    hotelPlanList?.forEach((plan) => plan.code && planCodes.add(plan.code))
    otherHotelPlanList?.forEach((plan) => plan.code && planCodes.add(plan.code))

    const combinedPlans: typeof hotelPlanList = []
    planCodes.forEach((code) => {
      const hotelPlan =
        hotelPlanList?.find((plan) => plan.code === code) ||
        otherHotelPlanList?.find((plan) => plan.code === code)
      if (hotelPlan) {
        combinedPlans.push(hotelPlan)
      }
    })

    return combinedPlans
  }, [hotelPlanList, otherHotelPlanList])

  const { data: hotelList } = useQueryHotelList(
    areaId === AREA_OPTION_ALL ? null : (areaId as string | undefined),
  )

  // Get hotel ID for special hotel logic
  const hotelId = hotelList?.find(
    (item: any) => item.triplabotCode === hotelCode,
  )?.id

  const getDefaultDateRange = () => {
    // If URL has date parameters, use those — this also covers the payment
    // page in undated mode, whose URL carries the calendar-picked dates
    // alongside searchType=undated.
    if (checkIn && checkOut) {
      return [
        new Date(`${checkIn}T00:00:00` as string),
        new Date(`${checkOut}T23:59:59` as string),
      ]
    }
    // Undated searches have no dates and must not fall through to the
    // special-hotel/default date logic below.
    if (isUndated) {
      return [null, null]
    }

    if (shouldUseEbisuHotelDates(hotelId)) {
      const checkinDate = dayjs(EBISU_HOTEL_CONFIG.checkinDate)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
      const checkoutDate = checkinDate
        .add(1, 'day')
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
      return [checkinDate.toDate(), checkoutDate.toDate()]
    }

    if (shouldUseMercureHiroshimaHotelDates(hotelId)) {
      const checkinDate = dayjs(MERCURE_HIROSHIMA_HOTEL_CONFIG.checkinDate)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
      const checkoutDate = checkinDate
        .add(1, 'day')
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
      return [checkinDate.toDate(), checkoutDate.toDate()]
    }

    if (shouldUseShinjukuOpeningHotelDates(hotelId)) {
      const checkinDate = dayjs(SHINJUKU_OPENING_HOTEL_CONFIG.checkinDate)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
      const checkoutDate = checkinDate
        .add(1, 'day')
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
      return [checkinDate.toDate(), checkoutDate.toDate()]
    }

    if (shouldUseGotandaOpeningHotelDates(hotelCode)) {
      const checkinDate = dayjs(GOTANDA_OPENING_HOTEL_CONFIG.checkinDate)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
      const checkoutDate = checkinDate
        .add(1, 'day')
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
      return [checkinDate.toDate(), checkoutDate.toDate()]
    }

    // Apply special hotel logic for defaults
    if (shouldUseHanareHotelDates(hotelId)) {
      const checkinDate = dayjs(HANARE_HOTEL_CONFIG.checkinDate)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
      const checkoutDate = checkinDate
        .add(1, 'day')
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
      return [checkinDate.toDate(), checkoutDate.toDate()]
    }

    // Default behavior for all other hotels - return null to indicate no dates set
    return [null, null]
  }

  const defaultDateRange = getDefaultDateRange()

  const defaultAmenities = !isEmpty(qs.parse(amenities))
    ? (qs.parse(amenities) as { planList: string[]; roomTypeList: string[] })
    : {
        planList: [],
        roomTypeList: [],
      }

  const getDefaultNumberRoomGuest = (): {
    index: number
    numberOfAdult: number
    numberOfChildren: number
  }[] => {
    // If URL has room/guest parameters, use those
    if (!isEmpty(qs.parse(numberRoomGuest))) {
      const parsed = qs.parse(numberRoomGuest) as {
        [key: string]: {
          index: string
          numberOfAdult: string
          numberOfChildren: string
        }
      }

      return Object.values(parsed).map((item) => ({
        index: Number(item.index),
        numberOfAdult: Number(item.numberOfAdult),
        numberOfChildren: Number(item.numberOfChildren),
      }))
    }

    // Apply special hotel logic for defaults
    if (hotelId === HANARE_HOTEL_CONFIG.id) {
      return [
        {
          index: 1,
          numberOfAdult: HANARE_HOTEL_CONFIG.adults, // Special hotel: 2 adults
          numberOfChildren: 0,
        },
      ]
    }

    // Default behavior for all other hotels
    return DEFAULT_ROOM_GUEST_VALUE
  }

  const defaultNumberRoomGuest = getDefaultNumberRoomGuest()

  const getHotelByPlanCode = (hotelPlanCode?: string) => {
    const selectedHotelId = combinedPlanList?.find(
      (plan) => plan.code === hotelPlanCode,
    )?.hotelId
    return hotelList?.find((hotel) => hotel.id === selectedHotelId)
  }

  const getSelectedHotelCode = (selectedRoomList: RoomSelectInfo[]) => {
    return getHotelByPlanCode(selectedRoomList[0].hotelPlanCode)?.triplabotCode
  }

  const onModifySearchCondition = () => {
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.set('areaId', areaId)
    urlSearchParams.set('hotelCode', hotelCode)
    if (amenities) {
      urlSearchParams.set('amenities', amenities)
    }
    urlSearchParams.set('numberRoomGuest', numberRoomGuest)
    if (isUndated) {
      urlSearchParams.set('searchType', 'undated')
      urlSearchParams.set('nights', String(nights))
    } else {
      urlSearchParams.set('checkIn', formatDate(defaultDateRange?.[0]))
      urlSearchParams.set('checkOut', formatDate(defaultDateRange?.[1]))
    }
    urlSearchParams.set('type', defaultType)
    urlSearchParams.set('orderBy', defaultOrderBy)
    urlSearchParams.set('searchTimestamp', Date.now().toString())

    if (parsedSelectedHotelPlanCodes) {
      urlSearchParams.set(
        'hotelPlanCodes',
        stringifyHotelPlanCodes(parsedSelectedHotelPlanCodes) as string,
      )
    }

    // If it is resale plan, delete hotelPlanCodes
    // because resale plans are secret and we need to pass
    // hotelPlanCodes to room plan detail & payment page
    if (isResalePlan) {
      urlSearchParams.delete('hotelPlanCodes')
    }

    urlSearchParams.set('showResale', showResale?.toString() || 'false')
    urlSearchParams.set('notScrollToSearchResult', 'true')
    navigate(`/${locale}?${urlSearchParams.toString()}`)
  }

  const onViewDetail = (selectedRoomList: RoomSelectInfo[]) => {
    if (!areaId || (!isUndated && (!checkIn || !checkOut))) {
      return
    }
    const selectedHotelCode = getSelectedHotelCode(selectedRoomList)
    if (!selectedHotelCode) return

    const urlSearchParams = new URLSearchParams()
    urlSearchParams.set('areaId', areaId)
    urlSearchParams.set('hotelCode', selectedHotelCode)
    if (amenities) {
      urlSearchParams.set('amenities', amenities)
    }
    urlSearchParams.set('numberRoomGuest', numberRoomGuest)
    if (isUndated) {
      urlSearchParams.set('searchType', 'undated')
      urlSearchParams.set('nights', String(nights))
    } else {
      urlSearchParams.set('checkIn', checkIn)
      urlSearchParams.set('checkOut', checkOut)
    }
    urlSearchParams.set('type', 'plan')
    urlSearchParams.set('orderBy', '')
    urlSearchParams.set(
      'selectedRoomList',
      qs.stringify(groupRoomSelectList(selectedRoomList)),
    )
    urlSearchParams.set('showResale', showResale?.toString() || 'false')
    urlSearchParams.set('isResalePlan', isResalePlan?.toString() || 'false')

    if (parsedSelectedHotelPlanCodes) {
      urlSearchParams.set(
        'hotelPlanCodes',
        stringifyHotelPlanCodes(parsedSelectedHotelPlanCodes) as string,
      )
    }

    checkAndSetResaleHotelPlanCodesQueryParams(
      isResalePlan === 'true',
      selectedRoomList,
      urlSearchParams,
      parsedSelectedHotelPlanCodes,
    )

    navigate(`/${locale}/room-plan-detail?${urlSearchParams.toString()}`)
  }

  const roomSearchParams = {
    areaId: areaId as string,
    hotelCode: hotelCode as string,
    amenities: amenities as string,
    defaultAmenities: defaultAmenities,
    checkIn: checkIn as string,
    checkOut: checkOut as string,
    dateRange: defaultDateRange,
    numberRoomGuest: numberRoomGuest as string,
    defaultNumberRoomGuest,
    defaultType,
    type: type as string,
    orderBy: orderBy as string,
    defaultOrderBy: defaultOrderBy as string,
    searchTimestamp: searchTimestamp as string,
    selectedHotelPlanCodes: parsedSelectedHotelPlanCodes,
    notScrollToSearchResult: notScrollToSearchResult === 'true',
    showResale:
      typeof showResale === 'string' ? showResale === 'true' : undefined,
    isResalePlan: isResalePlan === 'true',
    isUndated,
    nights,
    onModifySearchCondition,
    onViewDetail,
  }

  return roomSearchParams
}

export default useRoomSearchParams
